import { Share2, MessageSquare, Link2, Music2 } from "lucide-react";
import { toast } from "sonner";
import { Events } from "@/lib/analytics";

/**
 * Compelling share message that teases the pre-launch deal, so anyone who
 * receives the link is motivated to reserve too.
 */
const SHARE_TEXT =
  "Check out SayItBetter — it analyzes your messages BEFORE you send and suggests calmer rewrites. You can also reserve 1 year of PRO for just $5.99 (limited pre-launch offer).";
const SHARE_URL = "https://sayitbetter.me";

/**
 * Shorter text for SMS — most carriers still charge per 160-char segment.
 */
const SMS_TEXT =
  "Check out SayItBetter — an AI coach that rewrites your texts so you don't send something you'll regret. Reserve 1 yr PRO for $5.99 (pre-launch): " +
  SHARE_URL;

/**
 * TikTok caption tuned for short-form social. Copies caption + opens TikTok upload.
 */
const TIKTOK_CAPTION =
  "POV: AI grades your text before you send it 😅 Try @SayItBetter — link in bio · " +
  SHARE_URL +
  " #fyp #texting #relationshiptips";

/**
 * Opens the user's default SMS app with a pre-written message.
 */
export function textToFriend(source = "unknown") {
  const encoded = encodeURIComponent(SMS_TEXT);
  const href = `sms:?&body=${encoded}`;
  Events.shareApp(`sms:${source}`);
  try {
    window.location.href = href;
  } catch (_) {
    toast.info("Open your Messages app and paste: " + SHARE_URL, { duration: 6000 });
  }
}

/**
 * Opens WhatsApp with a pre-written message (native app or web.whatsapp.com).
 * Uses `https://wa.me` which is universally supported.
 */
export function shareToWhatsApp(source = "unknown") {
  const encoded = encodeURIComponent(SMS_TEXT);
  Events.shareApp(`whatsapp:${source}`);
  try {
    window.open(`https://wa.me/?text=${encoded}`, "_blank", "noopener,noreferrer");
  } catch (_) {
    toast.info("Open WhatsApp and paste: " + SHARE_URL, { duration: 6000 });
  }
}

/**
 * "Share on TikTok" — there is no public sharing intent for TikTok web, so we
 * copy a TikTok-tuned caption to the clipboard and open TikTok's upload page.
 * The user records their video, then pastes the caption.
 */
export function shareToTikTok(source = "unknown") {
  Events.shareApp(`tiktok:${source}`);
  const open = () => {
    try {
      window.open("https://www.tiktok.com/upload", "_blank", "noopener,noreferrer");
    } catch (_) {
      /* noop */
    }
  };
  try {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(TIKTOK_CAPTION).then(
        () => {
          toast.success("Caption copied — paste it in TikTok!", { duration: 5000 });
          open();
        },
        () => {
          toast.info("Caption: " + TIKTOK_CAPTION, { duration: 8000 });
          open();
        }
      );
      return;
    }
  } catch (_) {
    /* noop */
  }
  toast.info("Caption: " + TIKTOK_CAPTION, { duration: 8000 });
  open();
}

/**
 * Copy the share link + message to the clipboard.
 */
export async function copyShareLink(source = "unknown") {
  const fullText = `${SHARE_TEXT} ${SHARE_URL}`;
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(fullText);
      Events.shareApp(`copy:${source}`);
      toast.success("Link copied — paste it anywhere!");
      return true;
    }
  } catch (_) { /* fall through */ }
  try {
    const ta = document.createElement("textarea");
    ta.value = fullText;
    ta.style.position = "fixed"; ta.style.left = "-9999px";
    document.body.appendChild(ta);
    ta.select();
    document.execCommand("copy");
    document.body.removeChild(ta);
    Events.shareApp(`copy_legacy:${source}`);
    toast.success("Link copied!");
    return true;
  } catch (_) {
    toast.info(SHARE_URL, { duration: 6000 });
    return false;
  }
}

export async function shareApp(source = "unknown") {
  const shareData = { title: "SayItBetter", text: SHARE_TEXT, url: SHARE_URL };
  const fullText = `${SHARE_TEXT} ${SHARE_URL}`;

  // Try native share first — only on browsers that support + permit it
  if (typeof navigator !== "undefined" && navigator.share) {
    try {
      // canShare check guards against iframe/permission issues
      if (!navigator.canShare || navigator.canShare(shareData)) {
        await navigator.share(shareData);
        Events.shareApp(`native_share:${source}`);
        return { ok: true, method: "native" };
      }
    } catch (e) {
      // User cancelled or permission denied — fall through to clipboard
      if (e && e.name === "AbortError") {
        return { ok: false, method: "cancelled" };
      }
      // fall through
    }
  }

  // Clipboard fallback (works in most modern browsers including iframes)
  try {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(fullText);
      Events.shareApp(`clipboard:${source}`);
      toast.success("Link copied — paste it anywhere!");
      return { ok: true, method: "clipboard" };
    }
  } catch (_) {
    // fall through to textarea fallback
  }

  // Last-resort textarea fallback for older browsers / restricted contexts
  try {
    const ta = document.createElement("textarea");
    ta.value = fullText;
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.left = "-9999px";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    if (ok) {
      Events.shareApp(`execcommand:${source}`);
      toast.success("Link copied — paste it anywhere!");
      return { ok: true, method: "execcommand" };
    }
  } catch (_) {
    /* noop */
  }

  // Final graceful failure — show the URL so the user can copy manually
  toast.info(`Share this link: ${SHARE_URL}`, { duration: 6000 });
  Events.shareApp(`fallback:${source}`);
  return { ok: false, method: "manual" };
}

/**
 * Prominent "Share with friends" card. Used on Home tab and post-analysis screen.
 */
export default function ShareAppCard({ source = "home_card", variant = "default" }) {
  const handleText = () => textToFriend(source);
  const handleTikTok = () => shareToTikTok(source);
  const handleCopy = () => copyShareLink(source);

  const ShareButtons = ({ size = "default" }) => {
    const isCompact = size === "compact";
    const btnCls = isCompact
      ? "flex-1 inline-flex items-center justify-center gap-1.5 text-[12px] font-semibold py-2 rounded-lg active:scale-95 transition-all"
      : "flex-1 inline-flex items-center justify-center gap-1.5 text-[13px] font-semibold py-2.5 rounded-xl active:scale-95 transition-all";
    return (
      <div className="flex gap-2">
        <button
          onClick={handleTikTok}
          data-testid={`share-tiktok-btn-${size}`}
          className={`${btnCls} bg-black hover:bg-[#1a1a1a] text-white shadow-sm`}
        >
          <Music2 size={isCompact ? 13 : 15} />
          TikTok
        </button>
        <button
          onClick={handleCopy}
          data-testid={`share-copy-btn-${size}`}
          className={`${btnCls} bg-[#EAF6F4] hover:bg-[#d4ebe7] text-[#1F3A5F]`}
        >
          <Link2 size={isCompact ? 13 : 15} />
          Copy Link
        </button>
        <button
          onClick={handleText}
          data-testid={`share-text-btn-${size}`}
          className={`${btnCls} bg-[#1F3A5F] hover:bg-[#2a4a75] text-white shadow-sm`}
        >
          <MessageSquare size={isCompact ? 13 : 15} />
          {isCompact ? "Text" : "Share via Text"}
        </button>
      </div>
    );
  };

  if (variant === "compact") {
    return (
      <div
        className="bg-white rounded-xl border border-[#E5E7EB] p-3"
        data-testid="share-app-compact"
      >
        <div className="flex items-center gap-2.5 mb-2.5">
          <div className="w-8 h-8 rounded-lg bg-[#EAF6F4] flex items-center justify-center flex-shrink-0">
            <Share2 size={15} className="text-[#4FB3A5]" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[13px] font-semibold text-[#1F3A5F] leading-tight">
              Share SayItBetter with a friend
            </p>
            <p className="text-[11px] text-[#6B7280] leading-snug">
              Know someone who could use a little more confidence before hitting send?
            </p>
          </div>
        </div>
        <ShareButtons size="compact" />
      </div>
    );
  }

  return (
    <div
      className="rounded-2xl p-5 shadow-soft border border-[#E5E7EB] bg-white relative overflow-hidden"
      data-testid="share-app-card"
    >
      <div className="flex items-start gap-3 mb-4">
        <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-[#4FB3A5] to-[#2F5BFF] flex items-center justify-center flex-shrink-0 shadow-sm">
          <Share2 size={18} className="text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-[16px] font-bold text-[#1F3A5F] leading-tight mb-1">
            Share SayItBetter with a friend
          </h3>
          <p className="text-[13px] text-[#6B7280] leading-snug">
            Know someone who could use a little more confidence before hitting send?
          </p>
        </div>
      </div>
      <ShareButtons size="default" />
    </div>
  );
}
